import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { SelectedPresetInfo, SelectionValue } from '../components/presets/presetOptionButtons';
import { getPresetTypesForTool } from '../components/presets/presetTypeConfig';
import { TOOLS } from '../constants';
import { videoGenerationModelsQuery$data } from '../queries/__generated__/videoGenerationModelsQuery.graphql';
import { AdOptions, GenerationResult, InputImageData, PresetSelectionState, ProductSideImage, ToolType, VideoOptions, VideoResult } from '../types';

/**
 * Build the initial selections for a tool synchronously from the `defaultPresetId`s declared
 * in TOOL_PRESET_CONFIG. Only the id is seeded — PresetDropdown looks up the matching preset
 * from its own fetched list and fills in the label + thumbnail. Seeding synchronously avoids
 * the empty-dropdown flicker that happens when waiting for useDefaultSelections to resolve.
 */
export function getInitialSelectionsForTool(toolId: ToolType | null): Record<string, SelectionValue> {
    if (!toolId) return {};
    const presetTypes = getPresetTypesForTool(toolId);
    const initial: Record<string, SelectionValue> = {};
    for (const t of presetTypes) {
        if (t.defaultPresetId) {
            initial[t.id] = { id: t.defaultPresetId };
        }
    }
    return initial;
}

type VideoGenerationModel = videoGenerationModelsQuery$data['videoGenerationModels'][number];

export type GlobalView = 'home' | 'library' | 'tool';
export type ToolView = 'editor' | 'gallery';

export interface ToolState {
    view: ToolView;
    isGenerating: boolean;
    /** Timestamp (Date.now()) when generation started, for progress tracking */
    generationStartedAt: number | null;
    results: (GenerationResult | VideoResult)[];
}

const defaultToolState: ToolState = {
    view: 'editor',
    isGenerating: false,
    generationStartedAt: null,
    results: []
};

export interface AIStudioState {
    selectedTool: ToolType | null;
    globalView: GlobalView;
    previousGlobalView: GlobalView;
    prompt: string;
    /** Map of selections: key is typeId, value is single preset or array (for multiSelect types) */
    selections: Record<string, SelectionValue>;
    /** Per-tool state: view, isGenerating, results */
    toolStates: Partial<Record<ToolType, ToolState>>;
    error: string | null;
}

/** Get a tool's state, falling back to defaults */
export function getToolState(state: AIStudioState, toolId?: ToolType | null): ToolState {
    const id = toolId ?? state.selectedTool;
    if (!id) return defaultToolState;
    return state.toolStates[id] ?? defaultToolState;
}

/** Return a new state with the given tool's state patched */
export function updateToolState(state: AIStudioState, toolId: ToolType, patch: Partial<ToolState>): AIStudioState {
    const current = state.toolStates[toolId] ?? defaultToolState;
    return {
        ...state,
        toolStates: {
            ...state.toolStates,
            [toolId]: { ...current, ...patch }
        }
    };
}

export const initialState: AIStudioState = {
    selectedTool: null,
    globalView: 'home',
    previousGlobalView: 'home',
    prompt: '',
    selections: {},
    toolStates: {},
    error: null
};

interface UseAIStudioStateParams {
    initialTool?: ToolType;
    sideImages?: ProductSideImage[];
    has3D: boolean;
    productId?: number;
    initialVideoModel?: VideoGenerationModel | null;
    autoCapture3D?: boolean;
}

export interface UseAIStudioStateReturn {
    state: AIStudioState;
    setState: Dispatch<SetStateAction<AIStudioState>>;

    /** Current tool's results (derived) */
    results: (GenerationResult | VideoResult)[];

    // Extra state
    selectedImageIndex: number;
    setSelectedImageIndex: Dispatch<SetStateAction<number>>;
    inputImages: InputImageData[];
    setInputImages: Dispatch<SetStateAction<InputImageData[]>>;
    isIframeMode: boolean;
    setIsIframeMode: Dispatch<SetStateAction<boolean>>;
    isFromCatalog: boolean;
    setIsFromCatalog: Dispatch<SetStateAction<boolean>>;
    viewMode: 'images' | '3d';
    setViewMode: Dispatch<SetStateAction<'images' | '3d'>>;
    isProductInputEditing: boolean;
    setIsProductInputEditing: Dispatch<SetStateAction<boolean>>;
    isAutoCapturing: boolean;
    setIsAutoCapturing: Dispatch<SetStateAction<boolean>>;
    videoOptions: VideoOptions | null;
    setVideoOptions: Dispatch<SetStateAction<VideoOptions | null>>;
    adOptions: AdOptions;
    setAdOptions: Dispatch<SetStateAction<AdOptions>>;
    /** Selected aspect ratio per image tool (product-staging, virtual-tryon, multi-image-fusion). Undefined = let the model decide. */
    imageAspectRatios: Partial<Record<ToolType, string>>;
    handleImageAspectRatioChange: (toolId: ToolType, aspectRatio: string | undefined) => void;

    // Derived state
    /** Whether the currently selected tool is generating */
    isGenerating: boolean;
    /** Timestamp when generation started for the current tool */
    generationStartedAt: number | null;
    /** The current tool's view (editor or gallery) */
    currentToolView: ToolView;
    /** Top-level navigation state */
    globalView: GlobalView;
    currentToolConfig: (typeof TOOLS)[number] | undefined;
    hasProductInput: boolean;
    productImageUrl: string | undefined;
    selectionState: PresetSelectionState;

    // Navigation callbacks
    goToHome: () => void;
    goToEditor: () => void;
    goToGallery: () => void;
    goToLibrary: () => void;
    goBack: () => void;

    // Other callbacks
    handleToolChange: (toolId: ToolType) => void;
    handlePromptChange: (prompt: string) => void;
    handlePresetSelect: (typeId: string, value: SelectedPresetInfo | null) => void;
    handleDeleteResult: (resultId: string) => void;
    handleReturnToCustomizer: () => void;
}

const defaultAdOptions: AdOptions = {
    language: 'en',
    title: '',
    subtitle: '',
    ctaText: ''
};

export function useAIStudioState({
    initialTool,
    sideImages,
    has3D,
    productId,
    initialVideoModel,
    autoCapture3D
}: UseAIStudioStateParams): UseAIStudioStateReturn {
    const [state, setState] = useState<AIStudioState>(() => {
        if (initialTool) {
            return {
                ...initialState,
                selectedTool: initialTool,
                globalView: 'tool'
            };
        }
        return initialState;
    });

    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [inputImages, setInputImages] = useState<InputImageData[]>([]);
    const [isIframeMode, setIsIframeMode] = useState(false);
    const [isFromCatalog, setIsFromCatalog] = useState(false);
    const [viewMode, setViewMode] = useState<'images' | '3d'>(has3D ? '3d' : 'images');
    const [isProductInputEditing, setIsProductInputEditing] = useState(false);
    const [isAutoCapturing, setIsAutoCapturing] = useState(autoCapture3D ?? has3D);
    const [videoOptions, setVideoOptions] = useState<VideoOptions | null>(() => {
        const duration = initialVideoModel?.allowedDurations[0];
        const model = initialVideoModel?.model;
        const resolution = initialVideoModel?.allowedResolutions[0];

        if (duration === undefined || !model || !resolution) {
            return null;
        }

        return {
            duration,
            model,
            resolution,
            aspectRatio: initialVideoModel?.allowedAspectRatios[0],
            generateAudio: false
        };
    });
    const [adOptions, setAdOptions] = useState<AdOptions>(defaultAdOptions);
    const [imageAspectRatios, setImageAspectRatios] = useState<Partial<Record<ToolType, string>>>({});

    // Derived state
    const productImageUrl = useMemo(() => {
        if (sideImages && sideImages.length > 0) {
            const index = Math.min(selectedImageIndex, sideImages.length - 1);
            return sideImages[index].url;
        }
        return undefined;
    }, [sideImages, selectedImageIndex]);

    const currentToolConfig = useMemo(() => {
        return TOOLS.find((t) => t.id === state.selectedTool);
    }, [state.selectedTool]);

    const hasProductInput = useMemo(() => {
        const hasImages = !!sideImages && sideImages.length > 0;
        const has3DAvailable = has3D && !!productId;
        return (hasImages || has3DAvailable) && !!currentToolConfig?.inputPresetType;
    }, [sideImages, has3D, productId, currentToolConfig]);

    const currentToolState = getToolState(state);
    const results = currentToolState.results;
    const isGenerating = currentToolState.isGenerating;
    const generationStartedAt = currentToolState.generationStartedAt;
    const currentToolView = currentToolState.view;
    const globalView: GlobalView = state.globalView === 'library' ? 'library' : !state.selectedTool ? 'home' : state.globalView;

    const selectionState: PresetSelectionState = useMemo(
        () => ({
            selections: state.selections
        }),
        [state.selections]
    );

    // --- Navigation callbacks ---

    const goToHome = useCallback(() => {
        setState((prev) => ({
            ...prev,
            selectedTool: null,
            globalView: 'home',
            selections: {},
            toolStates: {},
            prompt: ''
        }));
        setIsIframeMode(false);
        setIsProductInputEditing(false);
        // When a product is loaded (from testPage/DAM/catalog), preserve the product-sourced input
        // and catalog/auto-capture flags so the product stays loaded when the user re-enters a tool.
        // Only user-uploaded images are cleared to match the "fresh tool" expectation.
        if (productId) {
            setInputImages((prev) => prev.filter((img) => img.fromProduct));
        } else {
            setInputImages([]);
            setIsFromCatalog(false);
            setIsAutoCapturing(false);
        }
    }, [productId]);

    const goToEditor = useCallback(() => {
        setState((prev) => {
            const tool = prev.selectedTool ?? TOOLS[0].id;
            return {
                ...updateToolState(prev, tool, { view: 'editor' }),
                selectedTool: tool,
                globalView: 'tool'
            };
        });
    }, []);

    const goToGallery = useCallback(() => {
        setState((prev) => {
            if (!prev.selectedTool) return prev;
            return updateToolState(prev, prev.selectedTool, { view: 'gallery' });
        });
    }, []);

    const goToLibrary = useCallback(() => {
        setState((prev) => ({ ...prev, previousGlobalView: prev.globalView, globalView: 'library' }));
    }, []);

    /** Smart back: from library → restore the previous global view */
    const goBack = useCallback(() => {
        setState((prev) => {
            if (!prev.selectedTool) {
                return { ...prev, globalView: 'home' };
            }
            return { ...prev, globalView: prev.previousGlobalView };
        });
    }, []);

    // --- Other callbacks ---

    const handleToolChange = useCallback(
        (toolId: ToolType) => {
            setState((prev) => ({
                ...prev,
                selectedTool: toolId,
                prompt: '',
                selections: {},
                globalView: 'tool',
                error: null
            }));

            const isProductMode = !!productId;

            // In product mode, preserve inputImages across tool switches so the product-sourced
            // image isn't lost when briefly visiting a tool that doesn't use image input
            // (text-to-3d, multi-views-to-3d). Outside product mode, reset per tool.
            if (isProductMode) {
                // This is the case when from multi view we switch to a tool that doesn't support multi view, we want to keep the product images but remove any user uploaded images
                setInputImages((prev) => prev.filter((img) => img.fromProduct));
            } else {
                setInputImages([]);
            }

            setIsProductInputEditing(false);
        },
        [sideImages, has3D, productId]
    );

    const handlePromptChange = useCallback((prompt: string) => {
        setState((prev) => ({ ...prev, prompt }));
    }, []);

    const handleImageAspectRatioChange = useCallback((toolId: ToolType, aspectRatio: string | undefined) => {
        setImageAspectRatios((prev) => ({ ...prev, [toolId]: aspectRatio }));
    }, []);

    const handlePresetSelect = useCallback((typeId: string, value: SelectedPresetInfo | null) => {
        setState((prev) => ({
            ...prev,
            selections: {
                ...prev.selections,
                [typeId]: value
            }
        }));
    }, []);

    const handleDeleteResult = useCallback((resultId: string) => {
        setState((prev) => {
            if (!prev.selectedTool) return prev;
            const toolState = getToolState(prev);
            return updateToolState(prev, prev.selectedTool, {
                results: toolState.results.filter((r) => r.id !== resultId)
            });
        });
    }, []);

    const handleReturnToCustomizer = useCallback(() => {
        if (productId) {
            setIsIframeMode(true);
        }
    }, [productId]);

    return {
        state,
        setState,
        results,
        selectedImageIndex,
        setSelectedImageIndex,
        inputImages,
        setInputImages,
        isIframeMode,
        setIsIframeMode,
        isFromCatalog,
        setIsFromCatalog,
        viewMode,
        setViewMode,
        isGenerating,
        generationStartedAt,
        currentToolView,
        globalView,
        currentToolConfig,
        hasProductInput,
        productImageUrl,
        selectionState,
        goToHome,
        goToEditor,
        goToGallery,
        goToLibrary,
        goBack,
        handleToolChange,
        handlePromptChange,
        handlePresetSelect,
        handleDeleteResult,
        handleReturnToCustomizer,
        isProductInputEditing,
        setIsProductInputEditing,
        isAutoCapturing,
        setIsAutoCapturing,
        videoOptions,
        setVideoOptions,
        adOptions,
        setAdOptions,
        imageAspectRatios,
        handleImageAspectRatioChange
    };
}
